import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PrimaryBlueButton, SecondaryBlueButton } from "@velaro/velaro-component-lib";
import useApi from "@/hooks/useApi";

export default function MindbodyCreate() {
  const navigate = useNavigate();
  const api = useApi();

  const [displayName, setDisplayName] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [mindbodySiteId, setMindbodySiteId] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave() {
    if (!apiKey.trim()) { setError("API Key is required."); return; }
    if (!mindbodySiteId.trim() || isNaN(Number(mindbodySiteId))) {
      setError("Mindbody Site ID must be a number.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const resp = await api.admin.post("Mindbody/Configs", {
        displayName: displayName.trim() || "Mindbody",
        apiKey: apiKey.trim(),
        mindbodySiteId: Number(mindbodySiteId),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({})) as { error?: string };
        setError(body?.error ?? "Failed to save configuration.");
        return;
      }
      navigate("/integrations/mindbody");
    } catch {
      setError("An unexpected error occurred.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="max-w-xl mx-auto">
      <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-5">
        <div>
          <h2 className="text-base font-semibold text-slate-800">Connect Mindbody</h2>
          <p className="text-sm text-slate-500 mt-1">
            Enter your Mindbody API key and Site ID. You can find these in the{" "}
            <span className="font-medium">Mindbody Developer Portal</span> under your app credentials.
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Display Name</label>
            <input
              type="text"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g. My Studio"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              API Key <span className="text-red-500">*</span>
            </label>
            <input
              type="password"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Mindbody API key"
              value={apiKey}
              onChange={e => setApiKey(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Mindbody Site ID <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g. -99"
              value={mindbodySiteId}
              onChange={e => setMindbodySiteId(e.target.value)}
            />
            <p className="text-xs text-slate-400 mt-1">Your Mindbody business site number (negative for sandbox).</p>
          </div>
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <SecondaryBlueButton label="Cancel" onClick={() => navigate("/integrations/mindbody")} />
          <PrimaryBlueButton
            label={saving ? "Connecting…" : "Connect"}
            onClick={handleSave}
            disabled={saving}
          />
        </div>
      </div>
    </div>
  );
}
